import { FactoryProvider, inject, InjectionToken } from '@angular/core';

export interface TrackingService {
  /**
   * Initializes the tracking service underneath.
   * Before calling this method, nothing should be tracked.
   * One reason to force the application to inject the service and call this method is that the service is guaranteed to be initialized,
   * even if it's not used directly. This is often the case if you only want to track network/page-load/etc.
   */
  init(): void;

  /* Report an error to the service.*/
  trackError(error: Error, properties?: Record<string, any>): void;

  /* Report a custom event to the service. In addition to the name you can provide arbitrary properties.*/
  trackEvent(name: string, properties?: Record<string, any>): void;

  // Add global custom property, which will be sent with every event
  addGlobalCustomProperty(name: string, value: string): void;

  // Delete global custom property
  deleteGlobalCustomProperty(name: string): void;
}

/**
 * Use this token to inject the {@link TrackingService}.
 * The token must be provided by your application, see {@link provideApplicationInsights} for more details on how to set it up.
 */
export const TRACKING_SERVICE = new InjectionToken<TrackingService>('TrackingService');

/**
 * This service can be used as a fallback to a tracking service if none should be used.
 * The advantage of providing a noop service instead of not providing a service at all is that usages can rely on a tracking service being there at all times.
 * It can also be used for testing purposes.
 */
export class NoopTrackingService implements TrackingService {
  public init(): void {}

  public trackError(): void {}

  public trackEvent(): void {}

  public addGlobalCustomProperty(name: string, value: string): void {}

  public deleteGlobalCustomProperty(name: string): void {}
}

/**
 * Use this provider to add azure application insights to your application.
 * In order for this to work, you need to call {@link @celum/shared/shared/prepareApplicationInsights} before bootstrapping your application.
 * Additionally inject the service and call the `init` method in your app component to activate the tracking.
 * In case that azure insights is not instrumented (properties are not set), a noop tracking service is provided instead and the insights bundle is not loaded.
 */
export function provideApplicationInsights(): FactoryProvider {
  return {
    provide: TRACKING_SERVICE,
    useFactory: () => {
      const insights = (window as any).applicationInsights;
      return insights ? inject(insights) : new NoopTrackingService();
    }
  };
}

/** Common properties that can be added as global parameters or as metadata to specific events. */
export enum TrackingProperty {
  // Mandatory properties that should be set by all applications

  /** tenant context from within the event was triggered */
  TENANT_ID = 'tenantId',
  /** name of the service that the event belongs to, e.g. "portals", "experience", ... */
  SERVICE = 'service',
  /** oid of the user that triggered the event */
  USER_OID = 'userOid',

  // Optional properties

  /** name of the context that the event belongs to, e.g. "single-download", "multi-download" */
  CONTEXT = 'context',
  /** name of the page that the event belongs to, e.g. "portal-designer", "library-wizard", ... */
  PAGE = 'page',
  /** technical id of the page that the event belongs to, can be used if there is no stable name for a page */
  PAGE_ID = 'pageId',
  /** type of the page - can be used in combination with {@link PAGE_ID} to add more details */
  PAGE_TYPE = 'pageType',
  /** name of the tracked feature like review-task */
  FEATURE_NAME = 'feature-name'
}

/** Common events that can occur in multiple applications and are likely to be used for cross-application insights in the future. */
export enum TrackingEvents {
  /** used to track the initial page load of an application, if you want to track specific pages within your app, use {@see PAGE_NAVIGATION} */
  PAGE_LOAD = 'page-loaded',
  /** used for page navigations within an application */
  PAGE_NAVIGATION = 'page-navigation',
  /** used for a single asset download */
  ASSET_DOWNLOAD = 'asset-download',
  /** used when more than 40 assets are downloaded at once */
  ASSET_BULK_DOWNLOAD = 'asset-bulk-download',
  /** used for viewing an asset separately */
  ASSET_VIEWED = 'asset-viewed',
  /** used when a scene is exported */
  SCENE_EXPORTED = 'scene-exported',
  /** used when a scene export fails with an error */
  SCENE_EXPORT_ERROR = 'scene-export-error',
  /** used when we interact with features like review-task */
  FEATURE_INTERACTION = 'feature-interaction'
}
